import styles from "./Tooltip.module.scss";
import React, { useEffect, useRef, useState } from "react";
import { useSpring, animated } from "react-spring";
import { Dot } from "../../assets/icons/Dot";
import { ChevronLeftIcon } from "../../assets/icons/ChevronLeftIcon";
import { ChevronRightIcon } from "../../assets/icons/ChevronRightIcon";
import { useTooltip } from "../../context/TooltipContext";

export const Tooltip = ({ id, stepsData, className }) => {
  const { activeTooltip, showTooltip, hideTooltip } = useTooltip();
  const isVisible = activeTooltip === id;

  const tooltipRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [enlargedImage, setEnlargedImage] = useState(false);

  const tooltipAnimation = useSpring({
    opacity: isVisible ? 1 : 0,
    transform: isVisible
      ? "translate(10px, -50%) scale(1)"
      : "translate(10px, -50%) scale(0.9)",
    config: { tension: 200, friction: 20 },
  });

  const dotAnimation = useSpring({
    opacity: isVisible ? 1 : 0,
    config: { tension: 200, friction: 20 },
  });

  const imageAnimation = useSpring({
    opacity: enlargedImage ? 1 : 0,
    transform: enlargedImage ? "scale(1)" : "scale(0.9)",
    config: { tension: 200, friction: 20 },
  });

  const handleToggleTooltip = () => {
    if (isVisible) {
      hideTooltip();
    } else {
      showTooltip(id);
    }
  };

  const toggleImageSize = (e) => {
    e.stopPropagation();
    setEnlargedImage(!enlargedImage);
  };

  const goToNextStep = () => {
    if (currentStep < stepsData.length) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Закрытие тултипа при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target) &&
        isVisible
      ) {
        hideTooltip();
        setEnlargedImage(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible, hideTooltip]);

  // Закрытие увеличенного изображения при нажатии ESC
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && enlargedImage) {
        setEnlargedImage(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [enlargedImage]);

  const { title, description, imgSrc } = stepsData[currentStep - 1] || {};

  return (
    <div className={styles.tooltipContainer}>
      <div onClick={handleToggleTooltip} className={styles.hoveredInfoBlock}>
        ?
      </div>

      {isVisible && (
        <div ref={tooltipRef}>
          <animated.div style={dotAnimation} className={styles.dot}>
            <Dot />
          </animated.div>
          <animated.div
            style={tooltipAnimation}
            className={`${styles.tooltip} ${className}`}
          >
            {imgSrc && (
              <div className={styles.imageContainer}>
                <img 
                  src={imgSrc} 
                  alt={`Step ${currentStep}`} 
                  onClick={toggleImageSize}
                  className={enlargedImage ? styles.enlargedImage : styles.normalImage}
                />
              </div>
            )}
            {title && <div className={styles.tooltipTitle}>{title}</div>}
            {description && (
              <div className={styles.tooltipText}>{description}</div>
            )}
            {stepsData.length > 1 && (
              <div className={styles.navigationButtons}>
                {currentStep > 1 ? (
                  <div onClick={goToPreviousStep} className={styles.prevButton}>
                    <ChevronLeftIcon width={15} height={20} fill={"white"} />
                  </div>
                ) : (
                  <div className={styles.prevButton}>
                    <ChevronLeftIcon width={15} height={20} fill={"#727272"} />
                  </div>
                )}
                {currentStep < stepsData.length ? (
                  <div onClick={goToNextStep} className={styles.nextButton}>
                    <ChevronRightIcon width={15} height={20} fill={"white"} />
                  </div>
                ) : (
                  <div className={styles.nextButton}>
                    <ChevronRightIcon width={15} height={20} fill={"#727272"} />
                  </div>
                )}
              </div>
            )}
            {stepsData.length > 1 && (
              <div className={styles.countDotsContainer}>
                {stepsData.map((item, index) => {
                  return (
                    <div
                      onClick={() => setCurrentStep(index + 1)}
                      key={`dots-${index}`}
                      className={`${styles.dots} ${
                        currentStep === index + 1 ? styles.active : ""
                      }`}
                    ></div>
                  );
                })}
              </div>
            )}
          </animated.div>
          
          {/* Модальное окно для увеличенного изображения */}
          {enlargedImage && imgSrc && (
            <animated.div 
              style={imageAnimation} 
              className={styles.enlargedImageOverlay}
              onClick={toggleImageSize}
            >
              <div className={styles.enlargedImageContainer}>
                <img 
                  src={imgSrc} 
                  alt={`Step ${currentStep} enlarged`} 
                  className={styles.fullSizeImage}
                />
              </div>
            </animated.div>
          )}
        </div>
      )}
    </div>
  );
};